import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../context/AuthContext';
import profileService from '../services/profilesService';
import './ProfilePage.css';

function getErrorMessage(error, fallback = 'Request failed.') {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    fallback
  );
}

function SecurityMessage({ message }) {
  if (!message?.text) return null;
  return (
    <div className={`profile-page__message profile-page__message--${message.type || 'info'}`}>
      {message.text}
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorQrCode, setTwoFactorQrCode] = useState('');
  const [twoFactorQrError, setTwoFactorQrError] = useState('');
  const [apiKeys, setApiKeys] = useState(null);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    re_new_password: '',
  });
  const [twoFactorForm, setTwoFactorForm] = useState({
    current_password: '',
    otp_code: '',
  });
  const [apiKeyPassword, setApiKeyPassword] = useState('');

  const [passwordMessage, setPasswordMessage] = useState(null);
  const [twoFactorMessage, setTwoFactorMessage] = useState(null);
  const [apiKeyMessage, setApiKeyMessage] = useState(null);
  const [submitting, setSubmitting] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const [twoFactorResponse, apiKeysResponse] = await Promise.all([
          profileService.getTwoFactorStatus({ signal: controller.signal }),
          profileService.getApiKeys({ signal: controller.signal }),
        ]);

        setTwoFactorEnabled(Boolean(twoFactorResponse?.data?.enabled));
        setApiKeys(apiKeysResponse?.data || null);
      } catch (error) {
        setApiKeyMessage({ type: 'error', text: getErrorMessage(error, 'Could not load profile settings.') });
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const otpauthUrl = twoFactorSetup?.otpauth_url;
    let isCurrent = true;

    setTwoFactorQrCode('');
    setTwoFactorQrError('');

    if (!otpauthUrl) {
      return () => {
        isCurrent = false;
      };
    }

    QRCode.toDataURL(otpauthUrl, {
      width: 192,
      margin: 2,
      color: {
        dark: '#0f172a',
        light: '#ffffff',
      },
    })
      .then((dataUrl) => {
        if (isCurrent) {
          setTwoFactorQrCode(dataUrl);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setTwoFactorQrError('Could not generate the QR code. Use the setup key instead.');
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [twoFactorSetup?.otpauth_url]);

  const updatePasswordForm = (field, value) => {
    setPasswordForm((current) => ({ ...current, [field]: value }));
  };

  const updateTwoFactorForm = (field, value) => {
    setTwoFactorForm((current) => ({ ...current, [field]: value }));
  };

  const onPasswordSubmit = async (event) => {
    event.preventDefault();
    setSubmitting('password');
    setPasswordMessage(null);

    try {
      await profileService.changePassword(passwordForm);
      setPasswordForm({ current_password: '', new_password: '', re_new_password: '' });
      setPasswordMessage({ type: 'success', text: 'Password updated.' });
    } catch (error) {
      setPasswordMessage({ type: 'error', text: getErrorMessage(error, 'Could not update password.') });
    } finally {
      setSubmitting('');
    }
  };

  const onStartTwoFactorSetup = async () => {
    setSubmitting('2fa-setup');
    setTwoFactorMessage(null);

    try {
      const response = await profileService.startTwoFactorSetup();
      setTwoFactorSetup(response?.data || null);
      setTwoFactorMessage({
        type: 'info',
        text: 'Scan the QR code with Google Authenticator, or enter the setup key manually, then confirm with the 6-digit code.',
      });
    } catch (error) {
      setTwoFactorMessage({ type: 'error', text: getErrorMessage(error, 'Could not start 2FA setup.') });
    } finally {
      setSubmitting('');
    }
  };

  const onEnableTwoFactor = async (event) => {
    event.preventDefault();
    setSubmitting('2fa-enable');
    setTwoFactorMessage(null);

    try {
      await profileService.enableTwoFactor({
        current_password: twoFactorForm.current_password,
        otp_code: twoFactorForm.otp_code,
        secret: twoFactorSetup?.secret,
      });
      setTwoFactorEnabled(true);
      setTwoFactorSetup(null);
      setTwoFactorForm({ current_password: '', otp_code: '' });
      setTwoFactorMessage({ type: 'success', text: 'Two-factor authentication enabled.' });
    } catch (error) {
      setTwoFactorMessage({ type: 'error', text: getErrorMessage(error, 'Could not enable 2FA.') });
    } finally {
      setSubmitting('');
    }
  };

  const onDisableTwoFactor = async (event) => {
    event.preventDefault();
    setSubmitting('2fa-disable');
    setTwoFactorMessage(null);

    try {
      await profileService.disableTwoFactor(twoFactorForm);
      setTwoFactorEnabled(false);
      setTwoFactorSetup(null);
      setTwoFactorForm({ current_password: '', otp_code: '' });
      setTwoFactorMessage({ type: 'success', text: 'Two-factor authentication disabled.' });
    } catch (error) {
      setTwoFactorMessage({ type: 'error', text: getErrorMessage(error, 'Could not disable 2FA.') });
    } finally {
      setSubmitting('');
    }
  };

  const onCreateApiKeys = async (event) => {
    event.preventDefault();
    setSubmitting('api-keys-create');
    setApiKeyMessage(null);

    try {
      const response = await profileService.createApiKeys({ current_password: apiKeyPassword });
      setApiKeys(response?.data || null);
      setApiKeyPassword('');
      setApiKeyMessage({
        type: 'success',
        text: 'New API keys created. Copy the secret now; it will not be shown again.',
      });
    } catch (error) {
      setApiKeyMessage({ type: 'error', text: getErrorMessage(error, 'Could not create new API keys.') });
    } finally {
      setSubmitting('');
    }
  };

  const onDeleteApiKeys = async () => {
    const shouldDelete = window.confirm('Delete your API keys? This cannot be undone.');
    if (!shouldDelete) {
      return;
    }

    setSubmitting('api-keys-delete');
    setApiKeyMessage(null);

    try {
      await profileService.deleteApiKeys({ current_password: apiKeyPassword });
      setApiKeys({ public_key: '', secret_key: '' });
      setApiKeyPassword('');
      setApiKeyMessage({ type: 'success', text: 'API keys deleted.' });
    } catch (error) {
      setApiKeyMessage({ type: 'error', text: getErrorMessage(error, 'Could not delete API keys.') });
    } finally {
      setSubmitting('');
    }
  };

  return (
    <div className="profile-page">
      <div className="card profile-page__header">
        <h1>Profile settings</h1>
        <p className="profile-page__muted">
          Signed in as {user?.email || user?.username || 'your account'}.
        </p>
      </div>

      {loading ? (
        <div className="card profile-page__section">Loading profile settings…</div>
      ) : (
        <>
          <section className="card profile-page__section">
            <div className="profile-page__section-heading">
              <div>
                <h2>Password</h2>
                <p className="profile-page__muted">Change the password used for web login.</p>
              </div>
            </div>

            <form className="profile-page__form" onSubmit={onPasswordSubmit}>
              <label>
                Current password
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => updatePasswordForm('current_password', e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>

              <label>
                New password
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => updatePasswordForm('new_password', e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              <label>
                Confirm new password
                <input
                  type="password"
                  value={passwordForm.re_new_password}
                  onChange={(e) => updatePasswordForm('re_new_password', e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              <button type="submit" disabled={submitting === 'password'}>
                {submitting === 'password' ? 'Saving…' : 'Update password'}
              </button>
            </form>
            <SecurityMessage message={passwordMessage} />
          </section>

          <section className="card profile-page__section">
            <div className="profile-page__section-heading">
              <div>
                <h2>Two-factor authentication</h2>
                <p className="profile-page__muted">
                  Status: {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>

            {!twoFactorEnabled ? (
              <>
                {!twoFactorSetup ? (
                  <button type="button" onClick={onStartTwoFactorSetup} disabled={submitting === '2fa-setup'}>
                    {submitting === '2fa-setup' ? 'Starting…' : 'Set up Google Authenticator'}
                  </button>
                ) : (
                  <form className="profile-page__form" onSubmit={onEnableTwoFactor}>
                    <div className="profile-page__two-factor-setup">
                      <div className="profile-page__qr-code">
                        <span>QR code</span>
                        {twoFactorQrCode ? (
                          <img src={twoFactorQrCode} alt="Google Authenticator setup QR code" />
                        ) : (
                          <div className="profile-page__qr-placeholder">Generating QR code…</div>
                        )}
                        {twoFactorQrError ? (
                          <p className="profile-page__qr-error">{twoFactorQrError}</p>
                        ) : null}
                      </div>

                      <div className="profile-page__setup-key">
                        <span>Setup key</span>
                        <code>{twoFactorSetup.secret}</code>
                      </div>
                    </div>
                    <p className="profile-page__muted">
                      If entering the setup key manually, use this account name:
                      {' '}
                      <code>{user?.email || user?.username}</code>.
                    </p>

                    <label>
                      Current password
                      <input
                        type="password"
                        value={twoFactorForm.current_password}
                        onChange={(e) => updateTwoFactorForm('current_password', e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </label>

                    <label>
                      6-digit authenticator code
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="6"
                        value={twoFactorForm.otp_code}
                        onChange={(e) => updateTwoFactorForm('otp_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoComplete="one-time-code"
                        required
                      />
                    </label>

                    <div className="profile-page__actions">
                      <button type="submit" disabled={submitting === '2fa-enable'}>
                        {submitting === '2fa-enable' ? 'Enabling…' : 'Enable 2FA'}
                      </button>
                      <button type="button" className="profile-page__secondary" onClick={() => setTwoFactorSetup(null)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <form className="profile-page__form" onSubmit={onDisableTwoFactor}>
                <label>
                  Current password
                  <input
                    type="password"
                    value={twoFactorForm.current_password}
                    onChange={(e) => updateTwoFactorForm('current_password', e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>

                <label>
                  6-digit authenticator code
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    value={twoFactorForm.otp_code}
                    onChange={(e) => updateTwoFactorForm('otp_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    required
                  />
                </label>

                <button type="submit" className="profile-page__danger" disabled={submitting === '2fa-disable'}>
                  {submitting === '2fa-disable' ? 'Disabling…' : 'Disable 2FA'}
                </button>
              </form>
            )}
            <SecurityMessage message={twoFactorMessage} />
          </section>

          <section className="card profile-page__section">
            <div className="profile-page__section-heading">
              <div>
                <h2>API keys</h2>
                <p className="profile-page__muted">
                  Use these keys for signed API access. The secret is shown only once, when keys are created.
                </p>
              </div>
            </div>

            <div className="profile-page__keys">
              <div>
                <span>Public key</span>
                <code>{apiKeys?.public_key || 'Not created'}</code>
              </div>
              <div>
                <span>Secret key</span>
                <code>
                  {apiKeys?.secret_key
                    ? apiKeys.secret_key
                    : apiKeys?.public_key
                      ? 'Hidden after creation'
                      : 'Not created'}
                </code>
              </div>
            </div>

            <form className="profile-page__form" onSubmit={onCreateApiKeys}>
              <label>
                Current password
                <input
                  type="password"
                  value={apiKeyPassword}
                  onChange={(e) => setApiKeyPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>

              <div className="profile-page__actions">
                <button type="submit" disabled={submitting === 'api-keys-create'}>
                  {submitting === 'api-keys-create' ? 'Creating…' : 'Create new API keys'}
                </button>
                <button
                  type="button"
                  className="profile-page__danger"
                  onClick={onDeleteApiKeys}
                  disabled={submitting === 'api-keys-delete' || !apiKeys?.public_key}
                >
                  {submitting === 'api-keys-delete' ? 'Deleting…' : 'Delete API keys'}
                </button>
              </div>
            </form>
            <SecurityMessage message={apiKeyMessage} />
          </section>
        </>
      )}
    </div>
  );
}
